/**
 * useWatchlist Hook
 * Manages watchlist functionality with backend integration
 */

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useMemo } from 'react';
import {
  setWatchlistGroup,
  optimisticAddToWatchlist,
  optimisticRemoveFromWatchlist,
  rollbackAddToWatchlist,
  rollbackRemoveFromWatchlist,
  loadWatchlistGroups,
  loadWatchlistItems,
  createWatchlistGroup,
  updateWatchlistGroup,
  deleteWatchlistGroupAsync,
  addWatchlistItem,
  removeWatchlistItem,
} from '../../../store/slices/marketDataSlice';

export interface UseWatchlistResult {
  /**
   * Current watchlist (instrument IDs) for the active group
   */
  watchlist: string[];
  /**
   * Watchlist groups
   */
  groups: Array<{ id: string; name: string; count: number; item_count: number; itemsLoaded: boolean }>;
  /**
   * Current active group
   */
  currentGroup: string | null;
  /**
   * Loading state for watchlist operations
   */
  loading: boolean;
  /**
   * Error state for watchlist operations
   */
  error: string | null;
  /**
   * Add instrument to watchlist (current group or specified group)
   */
  addInstrument: (instrumentId: string, groupId?: string) => Promise<void>;
  /**
   * Remove instrument from watchlist (current group)
   */
  removeInstrument: (instrumentId: string) => Promise<void>;
  /**
   * Set active watchlist group
   */
  setGroup: (groupId: string) => void;
  /**
   * Create new watchlist group
   */
  createGroup: (name: string) => Promise<void>;
  /**
   * Update watchlist group name
   */
  editGroupName: (groupId: string, name: string) => Promise<void>;
  /**
   * Delete watchlist group
   */
  deleteGroup: (groupId: string) => Promise<void>;
  /**
   * Check if instrument is in the CURRENT active group
   */
  isInCurrentGroup: (instrumentId: string) => boolean;
  /**
   * Check if instrument is in ANY of the user's watchlist groups.
   * Use this for search-result badges / global "already tracked" indicators.
   */
  isInAnyGroup: (instrumentId: string) => boolean;
  /**
   * @deprecated Use isInCurrentGroup instead
   */
  isInWatchlist: (instrumentId: string) => boolean;
  /**
   * Load watchlist groups (call on mount or when user changes)
   */
  loadGroups: () => Promise<void>;
  /**
   * Load watchlist items for a group
   */
  loadItems: (groupId: string) => Promise<void>;
}

export function useWatchlist(): UseWatchlistResult {
  const dispatch = useAppDispatch();
  const watchlistGroups = useAppSelector((state) => state.marketData.watchlistGroups);
  const currentGroup = useAppSelector((state) => state.marketData.currentGroup);
  const watchlistLoading = useAppSelector((state) => state.marketData.watchlistLoading);
  const watchlistError = useAppSelector((state) => state.marketData.watchlistError);

  // Get current group's watchlist
  const watchlist = useMemo(() => {
    if (currentGroup && watchlistGroups[currentGroup]) {
      return watchlistGroups[currentGroup].instruments;
    }
    return [];
  }, [currentGroup, watchlistGroups]);

  // Transform groups from Record to Array format for UI
  const groups = useMemo(() => {
    return Object.values(watchlistGroups).map((group) => ({
      id: group.id,
      name: group.name,
      count: group.instruments.length,
      item_count: group.item_count,
      itemsLoaded: group.itemsLoaded,
    }));
  }, [watchlistGroups]);

  const loadGroups = useCallback(
    async () => {
      await dispatch(loadWatchlistGroups()).unwrap();

      // After loading groups, load items for the current group if one is selected
      // Note: currentGroup is set in the reducer after groups are loaded
      // We'll load items in a separate effect or when group is selected
    },
    [dispatch]
  );

  const loadItems = useCallback(
    async (groupId: string) => {
      if (!groupId) return;
      await dispatch(loadWatchlistItems(groupId)).unwrap();
    },
    [dispatch]
  );

  const addInstrument = useCallback(
    async (instrumentId: string, groupId?: string) => {
      // If no explicit group is passed, use current selected group
      let targetGroup = groupId || currentGroup;
      if (!targetGroup) {
        const defaultGroup = Object.values(watchlistGroups).find(g => g.is_default);
        if (defaultGroup) {
          targetGroup = defaultGroup.id;
          dispatch(setWatchlistGroup(defaultGroup.id));
        } else if (Object.keys(watchlistGroups).length > 0) {
          // If no default group, use the first group
          targetGroup = Object.keys(watchlistGroups)[0];
          dispatch(setWatchlistGroup(targetGroup));
        } else {
          throw new Error('No group available');
        }
      }

      // Optimistic update
      dispatch(optimisticAddToWatchlist(instrumentId));

      try {
        await dispatch(
          addWatchlistItem({
            groupId: targetGroup,
            instrumentId,
          })
        ).unwrap();
      } catch (error) {
        // Rollback on error
        dispatch(rollbackAddToWatchlist(instrumentId));
        throw error;
      }
    },
    [dispatch, currentGroup, watchlistGroups]
  );

  const removeInstrument = useCallback(
    async (instrumentId: string) => {
      if (!currentGroup) {
        throw new Error('No group selected');
      }

      // Optimistic update
      dispatch(optimisticRemoveFromWatchlist(instrumentId));

      try {
        await dispatch(
          removeWatchlistItem({
            groupId: currentGroup,
            instrumentId,
          })
        ).unwrap();
      } catch (error) {
        // Rollback on error
        dispatch(rollbackRemoveFromWatchlist(instrumentId));
        throw error;
      }
    },
    [dispatch, currentGroup]
  );

  const setGroup = useCallback(
    (groupId: string) => {
      dispatch(setWatchlistGroup(groupId));
      // Load items for the selected group
      loadItems(groupId).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load watchlist items:', error);
      });
    },
    [dispatch, loadItems]
  );

  const createGroup = useCallback(
    async (name: string) => {
      const result = await dispatch(
        createWatchlistGroup({
          name,
        })
      ).unwrap();

      // Set the newly created group as current
      dispatch(setWatchlistGroup(result.id));
    },
    [dispatch]
  );

  const editGroupName = useCallback(
    async (groupId: string, name: string) => {
      await dispatch(
        updateWatchlistGroup({
          groupId,
          name,
        })
      ).unwrap();
    },
    [dispatch]
  );

  const deleteGroup = useCallback(
    async (groupId: string) => {
      await dispatch(deleteWatchlistGroupAsync(groupId)).unwrap();
    },
    [dispatch]
  );

  const isInCurrentGroup = useCallback(
    (instrumentId: string) => {
      return watchlist.includes(instrumentId);
    },
    [watchlist]
  );

  const isInAnyGroup = useCallback(
    (instrumentId: string) => {
      return Object.values(watchlistGroups).some((group) =>
        group.instruments.includes(instrumentId)
      );
    },
    [watchlistGroups]
  );

  // Keep isInWatchlist as a backward-compat alias for isInCurrentGroup
  const isInWatchlist = isInCurrentGroup;

  return {
    watchlist,
    groups,
    currentGroup,
    loading: watchlistLoading,
    error: watchlistError,
    addInstrument,
    removeInstrument,
    setGroup,
    createGroup,
    editGroupName,
    deleteGroup,
    isInWatchlist,
    isInCurrentGroup,
    isInAnyGroup,
    loadGroups,
    loadItems,
  };
}


